import * as createjs from 'createjs-module';
import { DragAndDrop, SymbolDragAndDrop, BaseDragAndDrop } from './draganddrop';
import { Evaluation } from '../../models/Evaluation';
import { Position } from '../../models/Story';

const FROZEN = 0;
const PLAYBACK = 1;
const TRACELOCATIONS = 2;

const BASEY = 90;
const BASEHEIGHT = 120;
const SYMBOLY = 71;

export const ZOOMSTATES = [0.35, 0.7];

declare interface DraggableElement {
	sprite: createjs.Bitmap | createjs.Shape;
	text: createjs.Text;
	dragAndDrop: DragAndDrop;
	evaluation: Evaluation;
}

export default class Draw {

	iterator: number;
	stage: createjs.Stage;
	mode: number;
	elementHistory: {element: DraggableElement, startingPosition: Position, positions: Position[]}[];
	zoomHistory: number[];
	base: DraggableElement;
	symbols: DraggableElement[];
	isStatic: boolean;
	replayFinishedCb: () => void;
	interval: any;
	zoomAmt: number;
	doubleTapping: boolean;

	constructor() {
		this.elementHistory = [];
		this.zoomHistory = [];
		this.mode = FROZEN;
		this.iterator = 0;
		this.zoomAmt = ZOOMSTATES[0];
	}

	resizeCanvas() {
		let canvas = document.getElementById('app') as HTMLCanvasElement;
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight - BASEY;
		this.base.sprite.y = window.innerHeight - BASEY - BASEHEIGHT;
		for (let i = 1; i < this.symbols.length; i++) {
			if (!this.symbols[i].dragAndDrop.used) {
				this.symbols[i].sprite.y = window.innerHeight - BASEY - SYMBOLY + 110;
			}
		}
	}

	init(evaluations: Evaluation[], isStatic: boolean) {
		this.stage = new createjs.Stage('app');
		this.isStatic = isStatic;

		createjs.Touch.enable(this.stage);
		let background = {sprite: undefined, dragAndDrop: undefined, evaluation: null, text: undefined};
		background.sprite = new createjs.Bitmap('/images/background.jpg');
		background.sprite.scaleX = ZOOMSTATES[0];
		background.sprite.scaleY = ZOOMSTATES[0];

		background.dragAndDrop = new DragAndDrop(background, isStatic, window.innerHeight - BASEY - BASEHEIGHT, true);

		this.stage.addChild(background.sprite);

		const base = this.paintSymbolBase();
		this.symbols = this.paintSymbols(background.dragAndDrop, base.dragAndDrop, evaluations, isStatic);
		this.symbols.unshift(background);

		for (let i = 0; i < this.symbols.length; i++) {
			this.elementHistory.push({element: this.symbols[i], startingPosition: {x: this.symbols[i].sprite.x, y: this.symbols[i].sprite.y}, positions: []});
		}

		this.resizeCanvas();
		window.removeEventListener('resize', this.resizeCanvas.bind(this), false);
		window.addEventListener('resize', this.resizeCanvas.bind(this), false);
		
		/*if (isTouchDevice()) {
			console.log("TOUCH DEVICE!");
		*/	this.stage.on('mousedown', this.dblClick.bind(this));

		/*} else {
			window.removeEventListener('dblclick', this.zoom.bind(this));
			window.addEventListener('dblclick', this.zoom.bind(this));
		}*/

		this.deleteInterval();

		const update = () => {
			this.stage.update();
			if (this.mode === TRACELOCATIONS) {
				this.trackLocations();
			} else if (this.mode === PLAYBACK) {
				this.playback();
			}
		};

		this.interval = setInterval(update, 20);
	}

	stop() {
		this.mode = FROZEN;
	}

	deleteInterval() {
		if (this.interval) {
			window.clearInterval(this.interval);
		}
	}

	paintSymbolBase() {
		this.base = {sprite: undefined, dragAndDrop: undefined, evaluation: null, text: undefined};
		this.base.sprite = new createjs.Shape();
		this.base.sprite.graphics.beginFill('#FFF').drawRect(-4000, 0, 10000, BASEHEIGHT);
		this.base.dragAndDrop = new BaseDragAndDrop(this.base);

		this.stage.addChild(this.base.sprite); 

		return this.base;
	}

	trackLocations() {
		let currentPositions = [];

		for (let i = 0; i < this.elementHistory.length; i++) {
			this.elementHistory[i].positions.push({x: this.elementHistory[i].element.sprite.x, y: this.elementHistory[i].element.sprite.y});
		}
		this.zoomHistory.push(this.zoomAmt);
	}

	start() {
		this.mode = TRACELOCATIONS;
	}

	initializeReplay(){
		for(let i = 1; i < this.elementHistory.length; i++) {
			(this.elementHistory[i].element.dragAndDrop as SymbolDragAndDrop).transformFromDrop();
		}
		this.repaintBase();
	}

	replay(cb: any) {
		this.replayFinishedCb = cb;
		// restore starting positions
		for (let i = 0; i < this.elementHistory.length; i++) {
			this.elementHistory[i].element.sprite.x = this.elementHistory[i].startingPosition.x;
			this.elementHistory[i].element.sprite.y = this.elementHistory[i].startingPosition.y;
		}
		this.iterator = 0;
		this.mode = PLAYBACK;
	}

	playback() {
		for (let i = 0; i < this.elementHistory.length; i++) {
			if (this.iterator < this.elementHistory[i].positions.length) {

				const element = this.elementHistory[i].element;

				//Check if should transform to a drop
				if(element.sprite.y < element.dragAndDrop.limit && element.sprite.y !== 0){
					element.dragAndDrop.transformIntoDrop(true);
					this.repaintBase();
					if(i > 0)
						element.dragAndDrop.used = true;
				}

				element.sprite.x = this.elementHistory[i].positions[this.iterator].x;
				element.sprite.y = this.elementHistory[i].positions[this.iterator].y;
				if(element.text && element.sprite.y > window.innerHeight - BASEY - SYMBOLY + 100)
					element.text.x = element.sprite.x - element.text.getBounds().width / 2;
			}else {
				this.replayFinishedCb();
			}
		}
		this.replayZoom(this.zoomHistory[this.iterator] || 0.5);
		this.iterator++;
	}

	paintSymbols(container: any, base: any, evaluations: Evaluation[], isStatic: boolean) {
		let symbols = [];
		for (let i = 0; i < evaluations.length; i++) {
			let circle = {sprite: undefined, dragAndDrop: undefined, evaluation: undefined, text: undefined};
			circle.sprite = new createjs.Bitmap(evaluations[i].image);
			let x = 150 * (i + 1);

			circle.sprite.x = x;
			circle.sprite.scaleX = 0.5;
			circle.sprite.scaleY = 0.5;
			
			circle.sprite.regX = 100;
			circle.sprite.regY = 320;

			circle.dragAndDrop = new SymbolDragAndDrop(
				circle, 
				{ x: x, y: window.innerHeight - BASEY - SYMBOLY + 110 },
				container,
				base,
				window.innerHeight - BASEY - BASEHEIGHT,
				isStatic,
				this,
				evaluations[i].drop
			);
			
			circle.evaluation = evaluations[i];
			symbols.push(circle);

			let preload = new createjs.Bitmap(evaluations[i].drop);
			preload.x = -1000;

			circle.text = new createjs.Text(evaluations[i].name, "14px Arial", "#000000");
			const b = circle.text.getBounds();
			circle.text.x = x - b.width / 2;
			circle.text.y = window.innerHeight - BASEY - 16;

			this.stage.addChild(circle.sprite);
			this.stage.addChild(circle.text);
		}
		return symbols;
	}

	repaintBase() {
		this.stage.removeChild(this.base.sprite);
		this.stage.addChild(this.base.sprite);
		for (let i = 1; i < this.symbols.length; i++){
			if (!(this.symbols[i].dragAndDrop as SymbolDragAndDrop).isDrop) {
				this.stage.removeChild(this.symbols[i].sprite);
				this.stage.addChild(this.symbols[i].sprite);
				this.stage.removeChild(this.symbols[i].text);
				this.stage.addChild(this.symbols[i].text);
			}
		}
	}

	zoom(event: any) {
		//if trying to access zoom while in replay mode
		if(this.isStatic) return;

		this.zoomAmt = this.zoomAmt === ZOOMSTATES[1] ? ZOOMSTATES[0] : ZOOMSTATES[1];

		const background = this.symbols[0];
		let multiplier;
		if(this.zoomAmt === ZOOMSTATES[1]){
			multiplier = ZOOMSTATES[1] / ZOOMSTATES[0];
		}else{
			multiplier = ZOOMSTATES[0] / ZOOMSTATES[1];
		}
		//zoomSpot.x /= multiplier;
		//zoomSpot.y /= multiplier;

		//background.sprite.scaleX = this.zoomAmt;
		//background.sprite.scaleY = this.zoomAmt;

		const bgx = (background.sprite.x - event.x) * multiplier + window.innerWidth / 2;
		const bgy = (background.sprite.y - event.y) * multiplier + window.innerHeight / 2;

		for (let i = 1; i < this.symbols.length; i++) {
			if(this.symbols[i].dragAndDrop.used){
				
				const symx = (this.symbols[i].sprite.x - background.sprite.x) * multiplier + bgx;
				const symy = (this.symbols[i].sprite.y - background.sprite.y) * multiplier + bgy;

				this.symbols[i].dragAndDrop.currentPosition.x = symx;
				this.symbols[i].sprite.x = symx;
				this.symbols[i].dragAndDrop.currentPosition.y = symy;
				this.symbols[i].sprite.y = symy;
			}
		}

		background.sprite.scaleX = this.zoomAmt;
		background.sprite.scaleY = this.zoomAmt;

		background.sprite.x = bgx;
		background.sprite.y = bgy;
		background.dragAndDrop.currentPosition.x = bgx;
		background.dragAndDrop.currentPosition.y = bgy;
	}

	dblClick(e) {
		if(this.doubleTapping){
			this.zoom({x: e.rawX, y: e.rawY});
		}else{
			this.doubleTapping = true;
			setTimeout(() => {
				this.doubleTapping = false;
			}, 200);
		}

	}

	replayZoom(zoomAmt: number){
		this.zoomAmt = zoomAmt;
		const background = this.symbols[0];
		background.sprite.scaleX = zoomAmt;
		background.sprite.scaleY = zoomAmt;
	}
}

function isTouchDevice() {  
	try {  
		document.createEvent("TouchEvent");  
		return true;  
	} catch (e) {  
		return false;  
	}  
}


// WEBPACK FOOTER //
// ./~/tslint-loader!./react/narrapix/src/containers/Play/draw.tsx